import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useWalletDiscovery, EIP6963ProviderDetail } from '@/hooks/useWalletDiscovery';
import { Loader2, RefreshCw, ExternalLink } from 'lucide-react';

interface WalletModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectWallet: (wallet: EIP6963ProviderDetail) => Promise<void>;
}

export function WalletModal({ open, onOpenChange, onSelectWallet }: WalletModalProps) {
  const { wallets, isDiscovering, refresh, hasWallets } = useWalletDiscovery();
  const [connecting, setConnecting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSelectWallet = async (wallet: EIP6963ProviderDetail) => {
    setConnecting(wallet.info.uuid);
    setError(null);

    try {
      await onSelectWallet(wallet);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to connect wallet';
      setError(message);
    } finally {
      setConnecting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Connect Wallet</DialogTitle>
          <DialogDescription>
            Select a wallet to connect to Prediction Mining
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-2">
          {isDiscovering ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Discovering wallets...</span>
            </div>
          ) : hasWallets ? (
            <>
              {wallets.map((wallet) => (
                <Button
                  key={wallet.info.uuid}
                  variant="outline"
                  className="w-full justify-start h-14 px-4"
                  onClick={() => handleSelectWallet(wallet)}
                  disabled={connecting !== null}
                >
                  <div className="flex items-center gap-3 w-full">
                    <img
                      src={wallet.info.icon}
                      alt={wallet.info.name}
                      className="w-8 h-8 rounded-lg"
                      onError={(e) => {
                        // Fallback icon if image fails to load
                        e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23666" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M22 10H2"/></svg>';
                      }}
                    />
                    <span className="font-medium">{wallet.info.name}</span>
                    {connecting === wallet.info.uuid && (
                      <Loader2 className="ml-auto h-4 w-4 animate-spin" />
                    )}
                  </div>
                </Button>
              ))}

              <div className="flex justify-center pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={refresh}
                  className="text-muted-foreground"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8 space-y-4">
              <p className="text-muted-foreground">
                No wallet detected. Please install a Web3 wallet.
              </p>
              <div className="flex flex-col gap-2">
                <a
                  href="https://metamask.io/download/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center text-primary hover:underline"
                >
                  Get MetaMask
                  <ExternalLink className="ml-1 h-3 w-3" />
                </a>
                <a
                  href="https://www.coinbase.com/wallet"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center text-primary hover:underline"
                >
                  Get Coinbase Wallet
                  <ExternalLink className="ml-1 h-3 w-3" />
                </a>
                <a
                  href="https://rabby.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center text-primary hover:underline"
                >
                  Get Rabby
                  <ExternalLink className="ml-1 h-3 w-3" />
                </a>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={refresh}
                className="text-muted-foreground"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t text-center">
          <p className="text-xs text-muted-foreground">
            By connecting your wallet, you agree to our{' '}
            <Link to="/terms" className="text-primary hover:underline" onClick={() => onOpenChange(false)}>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-primary hover:underline" onClick={() => onOpenChange(false)}>
              Privacy Policy
            </Link>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
